/**
 * Cart Notification Popup
 * 
 * Handles displaying and managing the "Added to Basket" popup
 * when a product is added to cart.
 * 
 * Features:
 * - Integrates with Shopify Cart API
 * - Accessible modal with focus trap
 * - Keyboard navigation (Escape to close)
 * - Updates dynamically with cart data
 */

// Guard against duplicate script loading (e.g. main-product.liquid loading this file more than once)
if (typeof window.CartNotificationPopup === 'undefined') {

class CartNotificationPopup extends HTMLElement {
  constructor() {
    super();
    this.popup = null;
    this.overlay = null;
    this.closeBtn = null;
    this.continueBtn = null;
    this.focusableElements = [];
    this.firstFocusable = null;
    this.lastFocusable = null;
    this.previouslyFocused = null;
    this.isOpen = false;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Get DOM elements
    this.popup = document.querySelector('[data-cart-notification-popup]');
    this.overlay = document.querySelector('[data-cart-notification-overlay]');
    this.closeBtn = document.querySelector('[data-cart-notification-close]');
    this.continueBtn = document.querySelector('[data-cart-notification-continue]');

    if (!this.popup) return;

    // Bind event listeners
    this.bindEvents();

    // Listen for custom cart add events
    this.listenForCartAdd();
  }

  bindEvents() {
    // Close button click
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Continue button click
    if (this.continueBtn) {
      this.continueBtn.addEventListener('click', () => this.close());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Keyboard events
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;
      
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
      }

      // Focus trap
      if (e.key === 'Tab') {
        this.handleTabKey(e);
      }
    });
  }

  listenForCartAdd() {
    // Method 1: Listen for custom event
    document.addEventListener('cart:item-added', (event) => {
      const { item, cart, quantity } = event.detail || {};
      this.updateAndOpen(item, cart, quantity);
    });

    // Method 2: Intercept form submissions
    this.interceptAddToCartForms();
  }

  interceptAddToCartForms() {
    // Observe for dynamically added forms
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      // Check if this form has data-type="add-to-cart-form"
      if (form.dataset.type !== 'add-to-cart-form') return;

      e.preventDefault();

      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
      }

      try {
        const formData = new FormData(form);

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          const sectionIds = cartDrawer.getSectionsToRender().map((section) => section.id);
          formData.set('sections', sectionIds.join(','));
          formData.set('sections_url', window.location.pathname);
          if (typeof cartDrawer.setActiveElement === 'function') {
            cartDrawer.setActiveElement(document.activeElement);
          }
        } else {
          formData.set('sections', 'cart-icon-bubble');
          formData.set('sections_url', window.location.pathname);
        }
        
        // Get quantity from the product page quantity selector
        // Priority: main quantity input > mobile quantity input > form quantity input
        const mainQuantityInput = document.getElementById('quantity-input');
        const mobileQuantityInput = document.getElementById('mobile-quantity-input');
        const formQuantityInput = form.querySelector('input[name="quantity"]');
        
        let selectedQuantity = 1;
        
        // Use the main quantity input as the source of truth (synced across all dropdowns)
        if (mainQuantityInput && mainQuantityInput.value) {
          selectedQuantity = parseInt(mainQuantityInput.value) || 1;
        } else if (mobileQuantityInput && mobileQuantityInput.value) {
          selectedQuantity = parseInt(mobileQuantityInput.value) || 1;
        } else if (formQuantityInput && formQuantityInput.value) {
          selectedQuantity = parseInt(formQuantityInput.value) || 1;
        }
        
        // Override the quantity in formData with the selected quantity
        formData.set('quantity', selectedQuantity);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const cartAddUrl = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add`;
        
        const response = await fetch(cartAddUrl, config);

        const item = await response.json();

        if (response.ok && !item.status) {
          // Optimistic badge update for immediate feedback
          this.incrementCartCountUI(selectedQuantity);

          // Apply rendered sections returned by the add-to-cart response (Shopify/Dawn pattern)
          const sectionsApplied = this.applySectionsFromResponse(item);

          // Fetch updated cart and reconcile exact count
          let cart = null;
          try {
            cart = await this.fetchCart();
            this.syncCartCountUI(cart.item_count);
          } catch (cartError) {
            console.warn('Cart fetch failed after add; keeping optimistic badge update.', cartError);
          }

          if (!sectionsApplied) {
            await this.refreshCartSections();
          }

          // Dispatch event for other components
          document.dispatchEvent(new CustomEvent('cart:item-added', {
            detail: { item, cart, quantity: selectedQuantity }
          }));

          // Also dispatch for cart drawer/icon updates
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart }
          }));
        } else {
          // Handle error
          console.error('Add to cart error:', item);
          this.showError(item.description || 'Could not add item to cart');
        }
      } catch (error) {
        console.error('Cart add failed:', error);
        this.showError('Something went wrong. Please try again.');
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.removeAttribute('aria-busy');
        }
      }
    });
  }

  applySectionsFromResponse(addResponse) {
    if (!addResponse?.sections) return false;

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') return false;

    let hasUpdatedSection = false;

    cartDrawer.getSectionsToRender().forEach((section) => {
      const sectionHTML = addResponse.sections[section.id];
      if (!sectionHTML) return;

      const targetElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!targetElement) return;

      const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const sourceElement = parsed.querySelector(section.selector || '.shopify-section');
      if (!sourceElement) return;

      targetElement.innerHTML = sourceElement.innerHTML;
      hasUpdatedSection = true;
    });

    if (hasUpdatedSection) {
      cartDrawer.bindOverlayClose?.();
      const hasLineItems = cartDrawer.querySelectorAll('tr.cart-item').length > 0;
      cartDrawer.classList.toggle('is-empty', !hasLineItems);
      cartDrawer.querySelector('cart-drawer-items')?.classList.toggle('is-empty', !hasLineItems);
    }

    return hasUpdatedSection;
  }

  updateAndOpen(item, cart, addedQuantity = null) {
    if (!this.popup) return;

    // Use the quantity that was just added (passed as parameter), not the total cart quantity
    const quantityToDisplay = addedQuantity || 1;

    // Update product image
    const imageEl = this.popup.querySelector('[data-cart-notification-image]');
    if (imageEl && item?.image) {
      imageEl.src = this.getSizedImageUrl(item.image, '116x116');
      imageEl.alt = item.title || '';
    }

    // Update product title
    const titleEl = this.popup.querySelector('[data-cart-notification-product-title]');
    if (titleEl && item?.title) {
      // Include variant title if exists
      let displayTitle = item.product_title || item.title;
      if (item.variant_title && item.variant_title !== 'Default Title') {
        displayTitle += ` - ${item.variant_title}`;
      }
      titleEl.textContent = displayTitle;
    }

    // Update line price - show only the quantity just added, not total cart quantity
    const linePriceEl = this.popup.querySelector('[data-cart-notification-line-price]');
    if (linePriceEl && item) {
      const price = item.price || 0;
      linePriceEl.textContent = `${quantityToDisplay} x ${this.formatMoney(price)}`;
    }

    // Update cart total
    const totalEl = this.popup.querySelector('[data-cart-notification-total]');
    if (totalEl && cart) {
      totalEl.textContent = `Basket Total: ${this.formatMoney(cart.total_price)}`;
    }

    // Update delivery banner
    this.updateDeliveryBanner(cart);

    // Open popup
    this.open();
  }

  updateDeliveryBanner(cart) {
    const banner = this.popup.querySelector('[data-cart-notification-delivery-banner]');
    if (!banner) return;

    const hasFreeDeliveryTag = this.popup.dataset.productHasFreeDelivery === 'true';
    if (!hasFreeDeliveryTag) {
      banner.style.display = 'none';
      return;
    }

    const threshold = parseInt(this.popup.dataset.freeDeliveryThreshold) || 0;
    const textEl = banner.querySelector('[data-cart-notification-delivery-text]');
    
    if (threshold > 0 && cart) {
      const cartTotal = cart.total_price / 100; // Convert from cents
      const remaining = threshold - cartTotal;

      if (remaining > 0) {
        textEl.textContent = `Spend ${this.formatMoney(remaining * 100)} more for FREE delivery`;
        banner.style.display = 'flex';
      } else {
        textEl.textContent = 'You qualify for FREE delivery';
        banner.style.display = 'flex';
      }
    }
  }

  open() {
    if (this.isOpen) return;

    // Store previously focused element
    this.previouslyFocused = document.activeElement;

    // Show popup and overlay
    this.popup.classList.add('is-active');
    this.overlay.classList.add('is-active');

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Set focus to close button
    this.setupFocusTrap();
    setTimeout(() => {
      this.closeBtn?.focus();
    }, 100);

    this.isOpen = true;

    // Announce to screen readers
    this.popup.setAttribute('aria-hidden', 'false');
  }

  close() {
    if (!this.isOpen) return;

    // Hide popup and overlay
    this.popup.classList.remove('is-active');
    this.overlay.classList.remove('is-active');

    // Restore body scroll
    document.body.style.overflow = '';

    // Restore focus
    if (this.previouslyFocused) {
      this.previouslyFocused.focus();
    }

    this.isOpen = false;

    // Announce to screen readers
    this.popup.setAttribute('aria-hidden', 'true');
  }

  setupFocusTrap() {
    this.focusableElements = this.popup.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (this.focusableElements.length > 0) {
      this.firstFocusable = this.focusableElements[0];
      this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    }
  }

  handleTabKey(e) {
    if (!this.firstFocusable || !this.lastFocusable) return;

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === this.firstFocusable) {
        e.preventDefault();
        this.lastFocusable.focus();
      }
    } else {
      // Tab
      if (document.activeElement === this.lastFocusable) {
        e.preventDefault();
        this.firstFocusable.focus();
      }
    }
  }

  formatMoney(cents) {
    // Simple money formatting - adjust for your store's currency
    const amount = (cents / 100).toFixed(2);
    return `£ ${amount.replace('.00', '')}`;
  }

  getSizedImageUrl(src, size) {
    if (!src) return '';
    
    // Handle Shopify CDN images
    if (src.includes('shopify')) {
      return src.replace(/(_\d+x\d+)?(\.[^.]+)$/, `_${size}$2`);
    }
    
    return src;
  }

  showError(message) {
    // You can customize this to show an error toast or modal
    console.error(message);
    alert(message);
  }

  async fetchCart() {
    const cartResponse = await fetch(`/cart.js?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!cartResponse.ok) throw new Error('Failed to fetch cart');
    return cartResponse.json();
  }

  syncCartCountUI(itemCount) {
    const cartCountSelectors = [
      '.cart-count-bubble',
      '.cart-count',
      '[data-cart-count]',
      '.header__cart-count',
      '.header-cart-badge',
      '.header-mobile-cart-badge'
    ];

    cartCountSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        if (!el.classList.contains('svg-wrapper')) {
          el.textContent = itemCount;
          el.style.display = itemCount > 0 ? '' : 'none';
        }
      });
    });

    const desktopCartIcon = document.getElementById('cart-icon-bubble');
    if (desktopCartIcon) {
      let desktopBadge = desktopCartIcon.querySelector('.header-cart-badge, .cart-count-bubble');

      if (itemCount > 0 && !desktopBadge) {
        desktopBadge = document.createElement('span');
        desktopBadge.className = 'header-cart-badge';
        desktopCartIcon.appendChild(desktopBadge);
      }

      if (desktopBadge) {
        desktopBadge.textContent = itemCount;
        desktopBadge.style.display = itemCount > 0 ? '' : 'none';
      }
    }

    const mobileCartIcons = document.querySelectorAll('.header-mobile-cart');
    mobileCartIcons.forEach((mobileCartIcon) => {
      let mobileBadge = mobileCartIcon.querySelector('.header-mobile-cart-badge');

      if (itemCount > 0 && !mobileBadge) {
        mobileBadge = document.createElement('span');
        mobileBadge.className = 'header-mobile-cart-badge';
        mobileCartIcon.appendChild(mobileBadge);
      }

      if (mobileBadge) {
        mobileBadge.textContent = itemCount;
        mobileBadge.style.display = itemCount > 0 ? '' : 'none';
      }
    });
  }

  incrementCartCountUI(delta = 1) {
    if (!Number.isFinite(delta) || delta <= 0) return;

    const badge = document.querySelector(
      '#cart-icon-bubble .header-cart-badge, #cart-icon-bubble .cart-count-bubble, .header-mobile-cart-badge, .header-cart-badge, .cart-count-bubble, [data-cart-count], .cart-count, .header__cart-count'
    );
    const currentValue = parseInt((badge?.textContent || '0').replace(/[^0-9]/g, ''), 10) || 0;
    this.syncCartCountUI(currentValue + delta);
  }

  /**
   * Refresh cart sections using Shopify's section rendering API
   * This ensures the cart drawer and icon bubble reflect the current cart state
   */
  async refreshCartSections() {
    try {
      const sections = 'cart-drawer,cart-icon-bubble';
      const response = await fetch(`${window.location.pathname}?sections=${sections}&ts=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch cart sections');
      
      const sectionsData = await response.json();
      
      // Update cart drawer content
      if (sectionsData['cart-drawer']) {
        const cartDrawerElement = document.getElementById('CartDrawer');
        if (cartDrawerElement) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionsData['cart-drawer'], 'text/html');
          const newContent = doc.querySelector('#CartDrawer');
          if (newContent) {
            cartDrawerElement.innerHTML = newContent.innerHTML;
          }
        }
      }
      
      // Update cart icon bubble
      if (sectionsData['cart-icon-bubble']) {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionsData['cart-icon-bubble'], 'text/html');
          const newIconContent = doc.querySelector('.shopify-section') || doc;
          if (newIconContent) {
            // Update just the badge, not the entire icon
            const newBadge = newIconContent.querySelector('.header-cart-badge, .cart-count-bubble');
            const existingBadge = cartIconBubble.querySelector('.header-cart-badge, .cart-count-bubble');
            if (newBadge && existingBadge) {
              existingBadge.textContent = newBadge.textContent;
              existingBadge.style.display = '';
            } else if (newBadge) {
              // Badge didn't exist before, need to add it
              cartIconBubble.appendChild(newBadge.cloneNode(true));
            } else if (!newBadge && existingBadge) {
              existingBadge.remove();
            }
          }
        }
      }

      // Also update cart count in header directly
      let cart = null;
      try {
        cart = await this.fetchCart();
      } catch (cartError) {
        console.warn('Cart fetch failed during section refresh; preserving current badge.', cartError);
      }

      // Sync empty state class on the <cart-drawer> wrapper
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cart) {
        const isEmpty = cart.item_count === 0;
        cartDrawer.classList.toggle('is-empty', isEmpty);
        cartDrawer.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);
      }

      if (cart) {
        this.syncCartCountUI(cart.item_count);
      }
    } catch (error) {
      console.error('Failed to refresh cart sections:', error);
    }
  }
}

// Register custom element
if (!customElements.get('cart-notification-popup')) {
  customElements.define('cart-notification-popup', CartNotificationPopup);
}

// Expose to global scope so the guard check works on subsequent script loads
window.CartNotificationPopup = CartNotificationPopup;

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  // Auto-initialize if the popup exists but isn't wrapped in custom element
  const popup = document.querySelector('[data-cart-notification-popup]');
  if (popup && !popup.closest('cart-notification-popup')) {
    const wrapper = new CartNotificationPopup();
    wrapper.init();
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartNotificationPopup;
}

} // end guard: typeof window.CartNotificationPopup === 'undefined'
